import React, { useCallback, memo, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import {useRedux} from "../../../hooks";
import {Label, Row,} from 'reactstrap';
import { Link } from 'react-router-dom'

import InputText from "../../../components/InputText";
import InputPassword from "../../../components/InputPassword";

import '../../../i18/config';
import {useTranslation} from "react-i18next";

import {
    Formik,
    Form,
    Field
} from 'formik';
import {LoginFormValidatorSchema} from "./LoginFormValidator";
import {LoginType} from "../../../redux/Main/Login/types";
import {INIT_LOGIN} from "../../../redux/Main/Login/reducer";
import {loginUser, loginPasskeyUser} from "../../../redux/Main/Login/actions";
import * as route from "../../../api/routes";
import { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, WebAuthnAbortService } from "@simplewebauthn/browser";
import { GET_CSRF_TOKEN } from "../../../api/urls";
import { APIClient, setCSRFToken } from "../../../api/apiCore";

const LoginForm = memo(() => {
    const { t } = useTranslation(['user','validator', 'auth', 'common']);
    const { dispatch, useAppSelector } = useRedux();

    const { isLoading } = useAppSelector(state => ({
        isLoading: state.Login.isLoading,
    }));

    const isBrowserSupportsWebAuthn = browserSupportsWebAuthn();

    const onDataSubmit = useCallback((formData: LoginType) => {
        dispatch(loginUser(formData));
    }, [dispatch]);

    const onPasskeyLoginSubmit = useCallback((email: string) => {
        dispatch(loginPasskeyUser({ email }));
    }, [dispatch]);

    useEffect(() => {
        // Passkey autofill needs this form's `webauthn` input in the page. The
        // form unmounts when the saved-device list arrives, often before the
        // CSRF request returns, so start only while mounted and cancel the
        // pending request on unmount.
        let isMounted = true;

        const api = new APIClient();
        api.get(GET_CSRF_TOKEN).then((res: any) => {
            if (res && res.csrf_token) {
                setCSRFToken(res.csrf_token);
            }
        }).catch(() => {}).finally(() => {
            if (!isMounted || !isBrowserSupportsWebAuthn) {
                return;
            }
            browserSupportsWebAuthnAutofill().then((isSupported) => {
                if (isMounted && isSupported) {
                    dispatch(loginPasskeyUser({ useBrowserAutofill: true }));
                }
            });
        });

        return () => {
            isMounted = false;
            WebAuthnAbortService.cancelCeremony();
        };
    }, [dispatch, isBrowserSupportsWebAuthn]);

    return (
        <Formik
            initialValues={INIT_LOGIN}
            validationSchema={LoginFormValidatorSchema}
            onSubmit={(values, actions) => {
                onDataSubmit(values);
                actions.setSubmitting(false);
            }}
            enableReinitialize={false}
        >
            {({ values, errors, touched, setFieldValue, getFieldProps }) => (
                <Form noValidate={true} className="customValidation">
                    <Row>
                        <div className={"mb-3" + (touched.email ? " was-validated" : '')}>
                            <Label className="form-label" htmlFor="form-email-input">E-mail</Label>
                            <Field
                                id="form-email-input"
                                name="email"
                                placeholder={t('user:enterEmail')}
                                component={InputText}
                                value={getFieldProps('email').value}
                                onChange={(value: string) => {
                                    setFieldValue('email', value);
                                }}
                                autocomplete={"username webauthn"}
                                className={errors.email && touched.email && 'form-control is-invalid'}
                            />
                            {errors.email && touched.email ? (
                                <div className="invalid-feedback">
                                    {t('validator:' + errors.email)}
                                </div>
                            ) : touched.email && !errors.email ?
                                <div className="valid-feedback">
                                    {t('common:looksGood')}
                                </div>
                                : ''
                            }
                        </div>
                    </Row>
                    <Row>
                        <div className={"mb-3" + (touched.password ? " was-validated" : '')}>
                            <div className="float-end">
                                <Link to={route.MAIN_AUTH_REQUEST_RESET_PASSWORD} tabIndex={-1} className="text-muted">{t('auth:forgotPassword')}?</Link>
                            </div>
                            <Label className="form-label" htmlFor="form-password-input">{t('user:password')}</Label>
                            <Field
                                id="form-password-input"
                                name="password"
                                placeholder={t('user:enterPassword')}
                                component={InputPassword}
                                value={getFieldProps('password').value}
                                onChange={(value: string) => {
                                    setFieldValue('password', value);
                                }}
                                className={errors.password && touched.password && 'form-control is-invalid'}
                            />
                            {errors.password && touched.password ? (
                                <div className="invalid-feedback">
                                    {t('validator:' + errors.password)}
                                </div>
                            ) : touched.password && !errors.password ?
                                <div className="valid-feedback">
                                    {t('common:looksGood')}
                                </div>
                                : ''
                            }
                        </div>
                    </Row>

                    <div className="mt-4">
                        <Button
                            type="submit"
                            disabled={isLoading}
                            variant="success"
                            className="w-100 mb-2">{t('auth:signIn')}</Button>

                        { isBrowserSupportsWebAuthn &&
                        <Button
                            type="button"
                            disabled={isLoading}
                            variant="outline-primary"
                            onClick={() => onPasskeyLoginSubmit(values.email)}
                            className="w-100">{t('auth:signInWithPasskey')}</Button>
                        }
                    </div>
                </Form>
            )}
        </Formik>
    );
});

export default LoginForm;
